import agent, { ListData } from "../api/agent";
import { DataTableList } from "../common/interfaces/TableData";
import { FilterProps } from "../common/DataTable";

export type PaymentStore = {
    tableData: DataTableList<any>;
    loading: boolean;

    uploadPaymentCsvData: (formData: FormData) => Promise<void>;
    loadUnLinkedPayments: (filterProps?: FilterProps) => Promise<ListData<any> | void>;
    deletePayment: (id: string) => Promise<void>;
    connectUnLinkedPayments: () => Promise<void>;
    createPayment: (payment: any) => Promise<void>;
    updatePayment: (payment: any) => Promise<void>;
    loadPaymentsForMember: (memberId: string, filterProps?: FilterProps) => Promise<ListData<any> | void>;
    loadPayment: (id: string) => Promise<any>;
};

export const createPaymentStore = (set: any, get: any): PaymentStore => ({
    tableData: new DataTableList<any>(),
    loading: false,

    uploadPaymentCsvData: async (formData: FormData) => {
        set((s: any) => ({ paymentStore: { ...s.paymentStore, loading: true } }));
        try {
            await agent.Payments.uploadPayments(formData);
        } finally {
            set((s: any) => ({ paymentStore: { ...s.paymentStore, loading: false } }));
        }
    },

    loadUnLinkedPayments: async (filterProps?: FilterProps) => {
        const applied = filterProps || {};
        set((s: any) => ({
            paymentStore: {
                ...s.paymentStore,
                loading: true,
                lastFilter: applied,
            }
        }));
        try {
            const res: ListData<any> = await agent.Payments.getUnlinkedMemberPayments(applied || {});
            const list = res.data.reduce((acc: Map<string, any>, item: any) => {
                acc.set(item.id!, item);
                return acc;
            }, new Map<string, any>());
            set((s: any) => ({
                paymentStore: {
                    ...s.paymentStore,
                    tableData: {
                        list,
                        currentPage: res.currentPage,
                        pageSize: res.pageSize,
                        totalRecords: res.totalRecords,
                        orderBy: res.orderBy,
                    },
                    loading: false,
                },
            }));
            return res;
        } catch (e) {
            console.error(e);
            set((s: any) => ({ paymentStore: { ...s.paymentStore, loading: false } }));
        }
    },

    deletePayment: async (id: string) => {
        await agent.Payments.delete(id);
        set((s: any) => {
            const list = new Map(s.paymentStore.tableData.list);
            list.delete(id);
            return {
                paymentStore: {
                    ...s.paymentStore,
                    tableData: { ...s.paymentStore.tableData, list },
                },
            };
        });
    },

    connectUnLinkedPayments: async () => {
        set((s: any) => ({ paymentStore: { ...s.paymentStore, loading: true } }));
        try {
            await agent.Payments.connectUnlinkedPayments();
        } finally {
            set((s: any) => ({ paymentStore: { ...s.paymentStore, loading: false } }));
        }
    },

    createPayment: async (payment: any) => {
        await agent.Payments.create(payment);
        set((s: any) => {
            const list = new Map(s.paymentStore.tableData.list);
            if (payment?.id) list.set(payment.id, payment);
            return {
                paymentStore: {
                    ...s.paymentStore,
                    tableData: { ...s.paymentStore.tableData, list },
                },
            };
        });
    },

    updatePayment: async (payment: any) => {
        await agent.Payments.update(payment);
        set((s: any) => {
            const list = new Map(s.paymentStore.tableData.list);
            if (payment?.id) {
                const existing = list.get(payment.id) || {};
                list.set(payment.id, { ...existing, ...payment });
            }
            return {
                paymentStore: {
                    ...s.paymentStore,
                    tableData: { ...s.paymentStore.tableData, list },
                },
            };
        });
    },

    loadPaymentsForMember: async (memberId: string, filterProps?: FilterProps) => {
        set((s: any) => ({ paymentStore: { ...s.paymentStore, loading: true } }));
        try {
            const res: ListData<any> = await agent.Payments.list(memberId, filterProps || {});
            const list = res.data.reduce((acc: Map<string, any>, item: any) => {
                acc.set(item.id!, item);
                return acc;
            }, new Map<string, any>());

            set((s: any) => ({
                paymentStore: {
                    ...s.paymentStore,
                    tableData: {
                        list,
                        currentPage: res.currentPage,
                        pageSize: res.pageSize,
                        totalRecords: res.totalRecords,
                        orderBy: res.orderBy,
                    },
                    loading: false,
                },
            }));
            return res;
        } catch (e) {
            console.error(e);
            set((s: any) => ({ paymentStore: { ...s.paymentStore, loading: false } }));
        }
    },

    loadPayment: async (id: string) => {
        const payment = await agent.Payments.details(id);
        return payment;
    },
});